/**
 * BHABY HEALTH CONNECT — API Service
 * Centralized API client for all healthcare endpoints.
 */

import type {
  ApiResponse, BookingCostSummary, LabTest, BookingTest,
  Consultation, Vitals, NurseTask, Notification, PatientDetails,
  LanguagePreference, PricingEntry, User
} from '../types';

const API_BASE = process.env.REACT_APP_API_URL || 'https://health.bhabygroup.co.tz/api';
export const apiBase = API_BASE;

/**
 * API error with the HTTP status and the server's structured error code
 * (e.g. `email_not_verified` from POST /users/login) so callers can branch
 * on the failure type instead of parsing message strings.
 */
export class ApiError extends Error {
  status: number;
  code?: string;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Refresh the JWT token using the current token.
 * Returns new token or null if refresh fails.
 */
async function refreshToken(): Promise<string | null> {
  const currentToken = localStorage.getItem('token');
  if (!currentToken) return null;

  try {
    const res = await fetch(`${API_BASE}/users/profile`, {
      headers: { Authorization: `Bearer ${currentToken}` },
    });
    if (res.ok) {
      const data = await res.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        const user = JSON.parse(localStorage.getItem('bhealth_user') || '{}');
        user.token = data.token;
        localStorage.setItem('bhealth_user', JSON.stringify(user));
        return data.token;
      }
    }
  } catch {
    // Refresh failed
  }
  return null;
}

/**
 * Helper: make authenticated API request with automatic token refresh on 401.
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Parse an HTTP `Retry-After` header.
 * Supports delta-seconds (the form express-rate-limit sends). Returns 0 when
 * the header is missing or unparseable, meaning "do not retry".
 */
const SHORT_RETRY_AFTER_SECONDS = 5;

function parseRetryAfter(value: string | null): number {
  if (!value) return 0;
  const seconds = Number.parseInt(value.trim(), 10);
  if (Number.isFinite(seconds) && seconds > 0) return seconds;
  const date = Date.parse(value);
  if (Number.isFinite(date)) {
    const delta = Math.ceil((date - Date.now()) / 1000);
    return delta > 0 ? delta : 0;
  }
  return 0;
}

/**
 * Helper: make authenticated API request with automatic token refresh on 401
 * and automatic retry with exponential backoff on 429 (rate limit).
 */
async function request<T>(
  endpoint: string,
  options: RequestInit = {},
  _retried = false,
  _rateLimited = false
): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((options.headers as Record<string, string>) || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // If 429 (rate limited), retry ONCE — but only when the server says the
  // window is short. The rate-limit window is 15 minutes, so blindly
  // retrying after a fixed 2s is guaranteed to fail again and doubles load
  // (a retry storm that made the Sep-2026 429 incident worse).
  if (res.status === 429 && !_rateLimited) {
    const retryAfterSeconds = parseRetryAfter(res.headers.get('Retry-After'));
    // Retry only for a genuinely short wait; otherwise surface it honestly.
    if (retryAfterSeconds > 0 && retryAfterSeconds <= SHORT_RETRY_AFTER_SECONDS) {
      await sleep(retryAfterSeconds * 1000);
      return request<T>(endpoint, options, _retried, true);
    }
    throw new ApiError(
      "The service is receiving too many requests right now. Please wait a moment and try again.",
      429,
      'rate_limited'
    );
  }

  // If 401 and haven't retried yet, attempt token refresh
  if (res.status === 401 && !_retried && endpoint !== '/users/login') {
    const newToken = await refreshToken();
    if (newToken) {
      return request<T>(endpoint, options, true);
    }
    // Refresh failed — clear auth and redirect to login
    localStorage.removeItem('token');
    localStorage.removeItem('bhealth_user');
    // Respect the /web basename (without it the browser lands on the marketing
    // site instead of the login page).
    const base = window.location.pathname.startsWith('/web') ? '/web' : '';
    window.location.href = `${base}/login`;
    throw new Error('Session expired. Please sign in again.');
  }

  if (!res.ok) {
    const error = await res.json().catch(() => ({} as Record<string, unknown>));
    const rawMessage = typeof error.message === 'string' ? error.message : typeof error.error === 'string' ? error.error : '';
    const technical = /^(HTTP\s*\d{3}|\d{3}\s|Cannot\s+(GET|POST|PUT|PATCH|DELETE)|TypeError|SyntaxError|NetworkError)/i.test(rawMessage.trim());
    const fallbackByStatus: Record<number, string> = {
      400: 'Please check the information you entered and try again.',
      403: 'You do not have permission to perform this action.',
      404: 'The requested information could not be found.',
      409: 'This action conflicts with the current record. Please refresh and try again.',
      422: 'Some information could not be accepted. Please review your entries.',
      429: 'Too many requests. Please wait a moment and try again.',
      500: 'Something went wrong on our server. Please try again.',
      502: 'The service is temporarily unavailable. Please try again.',
      503: 'The service is temporarily unavailable. Please try again.',
    };
    throw new ApiError(
      rawMessage && !technical ? rawMessage : fallbackByStatus[res.status] || 'We could not complete that request. Please try again.',
      res.status,
      typeof error.error === 'string' ? error.error : undefined
    );
  }

  return res.json();
}

// ========== Auth API ==========
export const authApi = {
  login: (email: string, password: string) =>
    request<any>('/users/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  register: (data: { name: string; email: string; password: string; role?: string }) =>
    request<any>('/users/register', { method: 'POST', body: JSON.stringify(data) }),
  verifyEmail: (email: string, otp: string) =>
    request<any>('/users/verify-email', { method: 'POST', body: JSON.stringify({ email, otp }) }),
  resendOtp: (email: string) =>
    request<any>('/users/resend-otp', { method: 'POST', body: JSON.stringify({ email }) }),
  forgotPassword: (email: string) =>
    request<any>('/users/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (email: string, otp: string, newPassword: string) =>
    request<any>('/users/reset-password', { method: 'POST', body: JSON.stringify({ email, otp, newPassword }) }),
  getProfile: () => request<ApiResponse<User>>('/users/profile'),
  updateProfile: (data: Record<string, unknown>) =>
    request<ApiResponse<User>>('/users/profile', { method: 'PUT', body: JSON.stringify(data) }),
};

// ========== Guest Access API ==========
// Guest Patient Access: temporary verified identity for one care journey.
// The token returned by verify/claim is stored by the caller exactly like a
// login token; booking/payment/consent then flow through the normal APIs.
export const guestApi = {
  start: (email: string, name?: string) =>
    request<any>('/guest/start', { method: 'POST', body: JSON.stringify({ email, name }) }),
  verify: (email: string, otp: string) =>
    request<any>('/guest/verify', { method: 'POST', body: JSON.stringify({ email, otp }) }),
  resend: (email: string) =>
    request<any>('/guest/resend', { method: 'POST', body: JSON.stringify({ email }) }),
  session: () => request<any>('/guest/session'),
  claim: (data: { password: string; email?: string; claimCode?: string }) =>
    request<any>('/guest/claim', { method: 'POST', body: JSON.stringify(data) }),
};

// ========== Purpose-bound location sharing API ==========
// Explicit consent only: the patient grants a single snapshot (geolocation or
// a manually entered address), which expires, and can be revoked at any time.
export const locationApi = {
  request: (data: { bookingId: number; purpose: string }) =>
    request<any>('/location/requests', { method: 'POST', body: JSON.stringify(data) }),
  getMine: () => request<any[]>('/location/requests/mine'),
  get: (id: number) => request<any>(`/location/requests/${id}`),
  grant: (id: number, data: { lat?: number; lng?: number; accuracyMeters?: number; addressText?: string }) =>
    request<any>(`/location/requests/${id}/grant`, { method: 'PUT', body: JSON.stringify(data) }),
  decline: (id: number) =>
    request<any>(`/location/requests/${id}/decline`, { method: 'PUT' }),
  revoke: (id: number) =>
    request<any>(`/location/requests/${id}/revoke`, { method: 'PUT' }),
};

// ========== Feedback API ==========
// Two separate domains: patient→clinician/service, and staff→platform
// (private; administrators only for the aggregate view).
export const feedbackApi = {
  getEligibility: (bookingId: number | string) =>
    request<any>(`/feedback/patient/booking/${bookingId}`),
  submit: (data: {
    bookingId: number;
    rating: number;
    comment?: string;
    serviceRating?: number;
    isComplaint?: boolean;
    complaintDetails?: string;
  }) => request<any>('/feedback/patient', { method: 'POST', body: JSON.stringify(data) }),
  getMine: () => request<any[]>('/feedback/patient/mine'),
  getReceived: () => request<any>('/feedback/received'),
  getComplaints: (status?: string) =>
    request<any[]>(`/feedback/complaints${status ? `?status=${status}` : ''}`),
  resolveComplaint: (id: number, resolution: string) =>
    request<any>(`/feedback/complaints/${id}/resolve`, { method: 'PUT', body: JSON.stringify({ resolution }) }),
  submitPlatform: (data: { category: string; message: string }) =>
    request<any>('/feedback/platform', { method: 'POST', body: JSON.stringify(data) }),
  getMyPlatform: () => request<any[]>('/feedback/platform/mine'),
  getPlatform: (status?: string) =>
    request<any[]>(`/feedback/platform${status ? `?status=${status}` : ''}`),
  advancePlatform: (id: number, status: string, response?: string) =>
    request<any>(`/feedback/platform/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, response }) }),
};

// ========== Insurance API ==========
export const insuranceApi = {
  getProfile: () => request<ApiResponse<any>>('/insurance/profile'),
  updateProfile: (data: Record<string, unknown>) =>
    request<ApiResponse<any>>('/insurance/profile', { method: 'PUT', body: JSON.stringify(data) }),
  getPending: () => request<ApiResponse<any[]>>('/insurance/pending'),
  verify: (uid: string, status: string, reason?: string) =>
    request<ApiResponse<any>>(`/insurance/${uid}/verify`, { method: 'PUT', body: JSON.stringify({ status, reason }) }),
  getAll: () => request<ApiResponse<any[]>>('/insurance'),
};

// ========== Patient API ==========
export const patientApi = {
  getProfile: () => request<ApiResponse<User & { patientDetails: PatientDetails | null }>>('/patients/profile'),
  getDetails: () => request<ApiResponse<PatientDetails>>('/patients/details'),
  updateDetails: (data: Partial<PatientDetails>) =>
    request<ApiResponse<PatientDetails>>('/patients/details', { method: 'PUT', body: JSON.stringify(data) }),
  updateEmergency: (data: { emergencyContactName: string; emergencyContactPhone: string; isEmergency: boolean; emergencyLevel: string }) =>
    request<ApiResponse<Record<string, unknown>>>('/patients/emergency', { method: 'PUT', body: JSON.stringify(data) }),
  recordVitals: (data: Partial<Vitals>) =>
    request<ApiResponse<{ id: number }>>('/patients/vitals', { method: 'POST', body: JSON.stringify(data) }),
  getVitalsByBooking: (bookingId: number) =>
    request<ApiResponse<Vitals[]>>(`/patients/vitals/booking/${bookingId}`),
  getVitalsHistory: (patientId: string, limit = 20) =>
    request<ApiResponse<Vitals[]>>(`/patients/vitals/history/${patientId}?limit=${limit}`),
  getFullProfile: (patientId: string) =>
    request<ApiResponse<Record<string, unknown>>>(`/patients/${patientId}/full-profile`),
  search: (q: string) => request<ApiResponse<User[]>>(`/patients/search?q=${encodeURIComponent(q)}`),
};

// ========== Clinical API ==========
export const clinicalApi = {
  createConsultation: (data: Partial<Consultation>) =>
    request<ApiResponse<{ id: number }>>('/clinical/consultations', { method: 'POST', body: JSON.stringify(data) }),
  getConsultation: (id: number) => request<ApiResponse<Consultation>>(`/clinical/consultations/${id}`),
  getConsultationsByBooking: (bookingId: number) =>
    request<ApiResponse<Consultation[]>>(`/clinical/consultations/booking/${bookingId}`),
  getPatientConsultations: (patientId: string, limit = 20) =>
    request<ApiResponse<Consultation[]>>(`/clinical/consultations/patient/${patientId}?limit=${limit}`),
  getDoctorConsultations: (limit = 20) =>
    request<ApiResponse<Consultation[]>>(`/clinical/consultations/doctor?limit=${limit}`),
  updateConsultation: (id: number, data: Partial<Consultation>) =>
    request<ApiResponse<Consultation>>(`/clinical/consultations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  savePrescription: (data: { bookingId: number; prescription: string; dosage?: string; duration?: string }) =>
    request<ApiResponse<Record<string, unknown>>>('/clinical/prescriptions', { method: 'POST', body: JSON.stringify(data) }),
  getPatientPrescriptions: (patientId: string) =>
    request<ApiResponse<Record<string, unknown>[]>>(`/clinical/prescriptions/patient/${patientId}`),
};

// ========== Laboratory API ==========
export const laboratoryApi = {
  getAllTests: (category?: string) =>
    request<ApiResponse<LabTest[]>>(`/laboratory/tests${category ? `?category=${category}` : ''}`),
  getTest: (id: number) => request<ApiResponse<LabTest>>(`/laboratory/tests/${id}`),
  createTest: (data: Partial<LabTest>) =>
    request<ApiResponse<{ id: number }>>('/laboratory/tests', { method: 'POST', body: JSON.stringify(data) }),
  updateTest: (id: number, data: Partial<LabTest>) =>
    request<ApiResponse<LabTest>>(`/laboratory/tests/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  orderTests: (bookingId: number, testIds: number[]) =>
    request<ApiResponse<{ tests: BookingTest[]; totalCost: number }>>('/laboratory/order', { method: 'POST', body: JSON.stringify({ bookingId, testIds }) }),
  getBookingTests: (bookingId: number) =>
    request<ApiResponse<{ tests: BookingTest[]; totalCost: number; pendingCount: number; totalCount: number }>>(`/laboratory/booking/${bookingId}`),
  approveTests: (bookingId: number) =>
    request<ApiResponse<{ approvedCount: number; totalCost: number }>>(`/laboratory/booking/${bookingId}/approve`, { method: 'POST' }),
  declineTests: (bookingId: number, reason: string) =>
    request<ApiResponse<{ declinedCount: number }>>(`/laboratory/booking/${bookingId}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),
  getPendingApproval: () => request<ApiResponse<BookingTest[]>>('/laboratory/pending-approval'),
  markPaid: (id: number) => request<ApiResponse<null>>(`/laboratory/tests/${id}/pay`, { method: 'PUT' }),
  markCollected: (id: number) => request<ApiResponse<null>>(`/laboratory/tests/${id}/collect`, { method: 'PUT' }),
  markCompleted: (id: number) => request<ApiResponse<null>>(`/laboratory/tests/${id}/complete`, { method: 'PUT' }),
  getCompletedTests: (patientId: string) => request<ApiResponse<BookingTest[]>>(`/laboratory/completed/${patientId}`),
};

// ========== Nurse API ==========
export const nurseApi = {
  createTask: (data: Partial<NurseTask>) =>
    request<ApiResponse<NurseTask>>('/nurse/tasks', { method: 'POST', body: JSON.stringify(data) }),
  getTask: (id: number) => request<ApiResponse<NurseTask>>(`/nurse/tasks/${id}`),
  getMyTasks: (status?: string) =>
    request<ApiResponse<NurseTask[]>>(`/nurse/tasks/my${status ? `?status=${status}` : ''}`),
  getAllTasks: (status?: string, taskType?: string) => {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    if (taskType) params.set('taskType', taskType);
    return request<ApiResponse<NurseTask[]>>(`/nurse/tasks?${params.toString()}`);
  },
  assignNurse: (taskId: number, nurseId: string) =>
    request<ApiResponse<NurseTask>>(`/nurse/tasks/${taskId}/assign`, { method: 'PUT', body: JSON.stringify({ nurseId }) }),
  startTask: (taskId: number) =>
    request<ApiResponse<NurseTask>>(`/nurse/tasks/${taskId}/start`, { method: 'PUT' }),
  completeTask: (taskId: number, notes?: string, messageToDoctor?: string) =>
    request<ApiResponse<NurseTask>>(`/nurse/tasks/${taskId}/complete`, { method: 'PUT', body: JSON.stringify({ notes, messageToDoctor }) }),
  updateSampleStatus: (taskId: number, status: 'SAMPLE_COLLECTED' | 'SAMPLE_SUBMITTED', notes?: string) =>
    request<ApiResponse<NurseTask>>(`/nurse/tasks/${taskId}/sample-status`, { method: 'PUT', body: JSON.stringify({ status, notes }) }),
  cancelTask: (taskId: number, reason?: string) =>
    request<ApiResponse<null>>(`/nurse/tasks/${taskId}/cancel`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  getStats: () => request<ApiResponse<{ assigned: number; inProgress: number; completed: number; cancelled: number }>>('/nurse/stats'),
  getAvailableNurses: () => request<ApiResponse<User[]>>('/nurse/available'),
};

// ========== Notifications API ==========
export const notificationsApi = {
  getMyNotifications: (params?: { limit?: number; unreadOnly?: boolean; type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
    if (params?.type) searchParams.set('type', params.type);
    const qs = searchParams.toString();
    return request<ApiResponse<{ notifications: Notification[]; unreadCount: number }>>(`/notifications${qs ? `?${qs}` : ''}`);
  },
  getUnreadCount: () => request<ApiResponse<{ count: number }>>('/notifications/unread-count'),
  markAsRead: (id: number) => request<ApiResponse<null>>(`/notifications/${id}/read`, { method: 'PUT' }),
  markAllAsRead: () => request<ApiResponse<{ updatedCount: number }>>('/notifications/read-all', { method: 'PUT' }),
  deleteNotification: (id: number) => request<ApiResponse<null>>(`/notifications/${id}`, { method: 'DELETE' }),
};

// ========== Pricing API ==========
export const pricingApi = {
  getAllPricing: (isTourist?: boolean) =>
    request<ApiResponse<PricingEntry[]>>(`/pricing${isTourist ? '?isTourist=true' : ''}`),
  getBookingSummary: (bookingId: number) =>
    request<ApiResponse<BookingCostSummary>>(`/pricing/booking-summary/${bookingId}`),
  updatePricing: (serviceType: string, data: { localPrice?: number; touristPrice?: number; fee?: number }) =>
    request<ApiResponse<PricingEntry[]>>(`/pricing/${encodeURIComponent(serviceType)}`, { method: 'PUT', body: JSON.stringify(data) }),
  getLabTestPricing: () => request<ApiResponse<PricingEntry[]>>('/pricing/lab-tests'),
};

// ========== Language API ==========
export const languageApi = {
  getAllLanguages: () =>
    request<ApiResponse<Array<{ language: string; userCount: number }>>>('/languages'),
  getMyPreferences: () =>
    request<ApiResponse<LanguagePreference[]>>('/languages/preferences'),
  setPreferences: (languages: LanguagePreference[]) =>
    request<ApiResponse<LanguagePreference[]>>('/languages/preferences', { method: 'PUT', body: JSON.stringify({ languages }) }),
  addLanguage: (language: LanguagePreference) =>
    request<ApiResponse<LanguagePreference[]>>('/languages/preferences', { method: 'POST', body: JSON.stringify(language) }),
  removeLanguage: (id: number) => request<ApiResponse<null>>(`/languages/preferences/${id}`, { method: 'DELETE' }),
  findDoctorsByLanguage: (language: string) =>
    request<ApiResponse<User[]>>(`/languages/doctors/${encodeURIComponent(language)}`),
  matchDoctors: () => request<ApiResponse<User[]>>('/languages/match-doctors'),
};

// ========== Payment API ==========
export const paymentApi = {
  initiate: (bookingId: number, phoneNumber: string) =>
    request<ApiResponse<any>>('/payments/initiate', {
      method: 'POST', body: JSON.stringify({ bookingId, phoneNumber }),
    }),
  getStatus: (orderId: string) =>
    request<ApiResponse<any>>(`/payments/status/${orderId}`),
};

// ========== Booking API (Doctor) ==========
export const bookingApi = {
  getDoctorBookings: () =>
    request<ApiResponse<any[]>>('/bookings/doctor'),
  getPendingBookings: () =>
    request<ApiResponse<any[]>>('/bookings/pending'),
  getBooking: (id: number) =>
    request<ApiResponse<any>>(`/bookings/${id}`),
  acceptBooking: (id: number, doctorId?: string, doctorName?: string) =>
    request<ApiResponse<any>>(`/bookings/${id}/accept`, {
      method: 'PUT',
      body: JSON.stringify({ doctorId, doctorName }),
    }),
  /** Decline a still-pending booking (doctor). Records it as cancelled + notifies the patient. */
  declineBooking: (id: number, reason?: string) =>
    request<ApiResponse<any>>(`/bookings/${id}/decline`, {
      method: 'PUT',
      body: JSON.stringify({ reason }),
    }),
  completeBooking: (id: number) =>
    request<ApiResponse<any>>(`/bookings/${id}/complete`, { method: 'PUT' }),
  updateBooking: (id: number, data: Record<string, unknown>) =>
    request<ApiResponse<any>>(`/bookings/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  getPatientBookings: () =>
    request<ApiResponse<any[]>>('/bookings/patient'),
  getAvailableDoctors: () =>
    request<ApiResponse<any[]>>('/bookings/doctors'),
  /** Versioned checkout consent catalog (server-authoritative wording). */
  getConsentCatalog: () =>
    request<Array<{ consentType: string; key: string; required: boolean; version: string; title: string; summaryKey: string; content: string; contentHash: string }>>('/bookings/consent-catalog'),
  /** Cancel booking (server enforces ownership + eligible states). */
  cancelBooking: (id: number, reason?: string) =>
    request<ApiResponse<any>>(`/bookings/${id}/cancel`, { method: 'PUT', body: JSON.stringify({ reason }) }),
};

// Invalidate the checkout token once its checkout is OVER (payment completed,
// or the wizard abandoned), so the next booking is a new checkout rather than a
// replay of the finished one.
//
// This is not optional bookkeeping: the token is a server-side idempotency key,
// and a stale one makes POST /api/bookings resolve to the PREVIOUS booking.
// Sep-2026: the token was never rotated, so a patient's second booking silently
// returned their first (already paid) booking and the payment screen reported
// success with no new order — while no new booking appeared.
export const resetCheckoutToken = (): void => {
  try {
    sessionStorage.removeItem('bhealth_checkout_token');
  } catch {
    // Storage unavailable (private mode / disabled) — nothing to clear; the
    // next getCheckoutToken() call falls back to a fresh in-memory token.
  }
};

// A random token that does NOT depend on Web Crypto being present.
//
// Resolving this inside the storage try/catch below meant that on any browser
// where `crypto` is absent (older WebViews, some embedded browsers) the
// ReferenceError skipped the sessionStorage write entirely: the token was
// never persisted, every submission got a brand-new one, and the server's
// double-booking guard could never fire.
const newCheckoutToken = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through to the non-crypto token
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
};

// Checkout idempotency token — stable across retries of ONE checkout, so a
// double-tap or network retry reuses the same checkout instead of creating a
// duplicate booking + payment pair. Retire it with resetCheckoutToken() the
// moment that checkout ends; it must never outlive the checkout it identifies.
export const getCheckoutToken = (): string => {
  const KEY = 'bhealth_checkout_token';
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
  } catch {
    // Storage unavailable — fall through and still return a usable token.
  }
  const token = newCheckoutToken();
  try {
    sessionStorage.setItem(KEY, token);
  } catch {
    // Could not persist (private mode) — the token is still valid for this call.
  }
  return token;
};

// ========== Scheduling API ==========
export const scheduleApi = {
  /** Get doctor's own schedule rules and overrides */
  getMySchedule: () =>
    request<{ rules: any[]; overrides: any[] }>('/schedule/my'),
  /** Bulk replace doctor's weekly schedule */
  replaceMySchedule: (rules: Array<{ dayOfWeek: number; startTime: string; endTime: string; slotDurationMinutes?: number; isActive?: boolean }>) =>
    request<{ rules: any[]; message: string }>('/schedule/my', {
      method: 'PUT', body: JSON.stringify({ rules })
    }),
  /** Add a single schedule rule */
  addScheduleRule: (rule: { dayOfWeek: number; startTime: string; endTime: string; slotDurationMinutes?: number }) =>
    request<any>('/schedule/my/rules', {
      method: 'POST', body: JSON.stringify(rule)
    }),
  /** Delete a schedule rule */
  deleteScheduleRule: (id: number) =>
    request<{ message: string }>(`/schedule/my/rules/${id}`, { method: 'DELETE' }),
  /** Create/update a date override */
  upsertOverride: (data: { overrideDate: string; isBlocked: boolean; startTime?: string; endTime?: string; reason?: string }) =>
    request<any>('/schedule/my/overrides', {
      method: 'POST', body: JSON.stringify(data)
    }),
  /** Delete an override */
  deleteOverride: (id: number) =>
    request<{ message: string }>(`/schedule/my/overrides/${id}`, { method: 'DELETE' }),
  /** Get available slots for a doctor */
  getAvailableSlots: (doctorId: string, startDate: string, endDate: string) =>
    request<Array<{ date: string; startTime: string; endTime: string; duration: number }>>(
      `/schedule/slots/${doctorId}?startDate=${startDate}&endDate=${endDate}`
    ),
  /** Check if a specific slot is available */
  checkSlot: (doctorId: string, date: string, time: string) =>
    request<{ available: boolean; reason?: string }>(
      `/schedule/check/${doctorId}?date=${date}&time=${time}`
    ),
  /**
   * Get the authenticated doctor's OWN available slots.
   *
   * Scoped server-side to the caller's uid, so the doctor's preview of what
   * patients see cannot be pointed at another doctor's calendar.
   */
  getMySlots: (startDate?: string, endDate?: string) => {
    const query = [
      startDate ? `startDate=${startDate}` : '',
      endDate ? `endDate=${endDate}` : '',
    ].filter(Boolean).join('&');
    return request<Array<{ date: string; startTime: string; endTime: string; duration: number }>>(
      `/schedule/my/slots${query ? `?${query}` : ''}`
    );
  },
  /** Next date (YYYY-MM-DD) with bookable slots for a doctor, or null when the horizon is exhausted */
  getNextAvailableDay: (doctorId: string, from?: string) =>
    request<{ date: string | null; slots: Array<{ date: string; startTime: string; endTime: string; duration: number }> }>(
      `/schedule/next/${doctorId}${from ? `?from=${from}` : ''}`
    ),
  /** Get doctor's public schedule */
  getDoctorSchedule: (doctorId: string) =>
    request<Array<{ dayOfWeek: number; startTime: string; endTime: string; slotDurationMinutes: number }>>(
      `/schedule/doctor/${doctorId}`
    ),
};

// ========== Wallet API ==========
export const walletApi = {
  getDoctorWallet: (uid: string) =>
    request<ApiResponse<any>>(`/wallet/doctor/${uid}`),
  requestWithdrawal: (data: { amount: number; paymentMethod: string; accountDetails: string }) =>
    request<ApiResponse<any>>('/wallet/withdraw', { method: 'POST', body: JSON.stringify(data) }),
};

// ========== Admin API ==========
export const adminApi = {
  getStats: () =>
    request<ApiResponse<{ total: number; pending: number; escalated: number; completed: number; doctors: number }>>('/admin/stats'),
  getBookings: () =>
    request<ApiResponse<any[]>>('/admin/bookings'),
  getUsers: () =>
    request<ApiResponse<any[]>>('/admin/users'),
  updateUserStatus: (uid: string, accountStatus: string) =>
    request<ApiResponse<any>>(`/admin/users/${uid}/status`, { method: 'PUT', body: JSON.stringify({ accountStatus }) }),
  getOnlineDoctors: () =>
    request<ApiResponse<any[]>>('/admin/users?role=doctor&isOnline=true'),
  getRevenue: () =>
    request<ApiResponse<any>>('/admin/revenue'),
  getSystemStats: () =>
    request<ApiResponse<any>>('/admin/system-stats'),
  /**
   * Provenance-classified platform analytics for one Tanzania (EAT) window.
   *
   * Every metric carries a provenance class, and an unmeasurable metric comes
   * back as null rather than 0 — the UI must render that as "not currently
   * available" so an unmeasured number is never read as a measured zero.
   */
  getAnalytics: (range: string = '30d') =>
    request<ApiResponse<any>>(`/admin/analytics?range=${encodeURIComponent(range)}`),
  approveDoctor: (uid: string) =>
    request<ApiResponse<any>>(`/admin/users/${uid}/approve-doctor`, { method: 'PUT' }),
  rejectDoctor: (uid: string, reason?: string) =>
    request<ApiResponse<any>>(`/admin/users/${uid}/reject-doctor`, { method: 'PUT', body: JSON.stringify({ reason: reason || 'Application does not meet requirements' }) }),
  getWithdrawals: () =>
    request<ApiResponse<any[]>>('/admin/withdrawals'),
  processWithdrawal: (id: number, status: string, adminNotes?: string) =>
    request<ApiResponse<any>>(`/admin/wallet/${id}/process`, { method: 'PUT', body: JSON.stringify({ status, adminNotes }) }),
};

// ========== Legal API (Consent & Compliance) ==========
export const legalApi = {
  recordConsent: (data: { bookingId: number; consentType: string; consentText: string; consentVersion?: string }) =>
    request<any>('/legal/consent', { method: 'POST', body: JSON.stringify(data) }),
  recordBulkConsents: (bookingId: number, consents: Array<{ type: string; text: string; version?: string }>) =>
    request<any>('/legal/consent/bulk', { method: 'POST', body: JSON.stringify({ bookingId, consents }) }),
  getConsentStatus: (bookingId: number) =>
    request<any>(`/legal/consent/status/${bookingId}`),
  getConsentHistory: () =>
    request<any[]>('/legal/consent/history'),
  getDocuments: (type?: string) =>
    request<any[]>(`/legal/documents${type ? `?type=${type}` : ''}`),
  getDocument: (id: number) => request<any>(`/legal/documents/${id}`),
  createDocument: (data: Record<string, unknown>) =>
    request<any>('/legal/documents', { method: 'POST', body: JSON.stringify(data) }),
};

// ========== Referral API ==========
export const referralApi = {
  create: (data: { bookingId: number; toDoctorId: string; reason: string; urgency?: string; clinicalSummary?: string; notes?: string }) =>
    request<any>('/referrals', { method: 'POST', body: JSON.stringify(data) }),
  getSent: (status?: string) =>
    request<any[]>(`/referrals/sent${status ? `?status=${status}` : ''}`),
  getReceived: (status?: string) =>
    request<any[]>(`/referrals/received${status ? `?status=${status}` : ''}`),
  // Patient visibility: referrals attached to the caller's own bookings only.
  getMine: () => request<any[]>('/referrals/mine'),
  get: (id: number) => request<any>(`/referrals/${id}`),
  accept: (id: number) =>
    request<any>(`/referrals/${id}/accept`, { method: 'PUT' }),
  decline: (id: number, reason?: string) =>
    request<any>(`/referrals/${id}/decline`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  complete: (id: number) =>
    request<any>(`/referrals/${id}/complete`, { method: 'PUT' }),
  getAvailableDoctors: (specialization?: string) =>
    request<any[]>(`/referrals/doctors${specialization ? `?specialization=${encodeURIComponent(specialization)}` : ''}`),
  // Nurse Handoffs
  createHandoff: (data: { bookingId: number; nurseId?: string; handoffType: string; clinicalNotes: string; instructions?: string; priority?: string }) =>
    request<any>('/referrals/handoff', { method: 'POST', body: JSON.stringify(data) }),
  getHandoffsSent: (status?: string) =>
    request<any[]>(`/referrals/handoff/sent${status ? `?status=${status}` : ''}`),
  getHandoffsReceived: (status?: string) =>
    request<any[]>(`/referrals/handoff/received${status ? `?status=${status}` : ''}`),
  getPendingHandoffs: () => request<any[]>('/referrals/handoff/pending'),
  acceptHandoff: (id: number) =>
    request<any>(`/referrals/handoff/${id}/accept`, { method: 'PUT' }),
  completeHandoff: (id: number) =>
    request<any>(`/referrals/handoff/${id}/complete`, { method: 'PUT' }),
  reassignHandoff: (id: number, newNurseId: string, reason?: string) =>
    request<any>(`/referrals/handoff/${id}/reassign`, { method: 'PUT', body: JSON.stringify({ newNurseId, reason }) }),
};

// ========== Messaging API ==========
export const messagingApi = {
  send: (data: { receiverId: string; bookingId?: number; messageType?: string; subject?: string; message: string }) =>
    request<any>('/messaging', { method: 'POST', body: JSON.stringify(data) }),
  getInbox: (limit?: number) =>
    request<any[]>(`/messaging/inbox${limit ? `?limit=${limit}` : ''}`),
  getSent: (limit?: number) =>
    request<any[]>(`/messaging/sent${limit ? `?limit=${limit}` : ''}`),
  getConversation: (userId: string) =>
    request<any[]>(`/messaging/conversation/${userId}`),
  get: (id: number) => request<any>(`/messaging/${id}`),
  getUnreadCount: () => request<{ count: number }>('/messaging/unread-count'),
  markAsRead: (id: number) =>
    request<any>(`/messaging/${id}/read`, { method: 'PUT' }),
  markAllAsRead: () =>
    request<any>('/messaging/read-all', { method: 'PUT' }),
  delete: (id: number) =>
    request<any>(`/messaging/${id}`, { method: 'DELETE' }),
  getContacts: () => request<any[]>('/messaging/contacts'),
};

// ========== Medical Records API ==========
export const recordsApi = {
  requestAccess: (data: { accessType: string; recordType: string; reason?: string }) =>
    request<any>('/records/access/request', { method: 'POST', body: JSON.stringify(data) }),
  getMyRequests: () => request<any[]>('/records/access/my-requests'),
  getPendingRequests: () => request<any[]>('/records/access/pending'),
  approveAccess: (id: number, expiresInHours?: number) =>
    request<any>(`/records/access/${id}/approve`, { method: 'PUT', body: JSON.stringify({ expiresInHours }) }),
  denyAccess: (id: number, denialReason?: string) =>
    request<any>(`/records/access/${id}/deny`, { method: 'PUT', body: JSON.stringify({ denialReason }) }),
  getDoctorAccessibleRecords: (patientId: string) =>
    request<any>(`/records/doctor/patient/${patientId}`),
  getFullPatientRecords: (patientId: string) =>
    request<any>(`/records/patient/${patientId}`),
};

// ========== Pharmacy API ==========
export const pharmacyApi = {
  getActiveContracts: () => request<any[]>('/pharmacy/contracts/active'),
  getAllContracts: (status?: string) =>
    request<any[]>(`/pharmacy/contracts${status ? `?status=${status}` : ''}`),
  getContract: (id: number) => request<any>(`/pharmacy/contracts/${id}`),
  createContract: (data: Record<string, unknown>) =>
    request<any>('/pharmacy/contracts', { method: 'POST', body: JSON.stringify(data) }),
  updateContractStatus: (id: number, status: string) =>
    request<any>(`/pharmacy/contracts/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  sendPrescription: (data: { bookingId: number; pharmacyId?: number; prescriptionText: string; notes?: string }) =>
    request<any>('/pharmacy/prescriptions', { method: 'POST', body: JSON.stringify(data) }),
  getPatientPrescriptions: (patientId: string) =>
    request<any[]>(`/pharmacy/prescriptions/patient/${patientId}`),
  getAllPrescriptions: (status?: string) =>
    request<any[]>(`/pharmacy/prescriptions${status ? `?status=${status}` : ''}`),
  updatePrescriptionStatus: (id: number, status: string, notes?: string) =>
    request<any>(`/pharmacy/prescriptions/${id}/status`, { method: 'PUT', body: JSON.stringify({ status, notes }) }),
};

// ========== Socket.io Helper ==========
export function connectSocket(_userId?: string): Promise<import('socket.io-client').Socket> {
  return new Promise(async (resolve, reject) => {
    try {
      const { io } = await import('socket.io-client');
      const token = localStorage.getItem('token');
      if (!token) return reject(new Error('Authentication required for realtime connection'));
      const socket = io(API_BASE.replace('/api', ''), {
        transports: ['websocket'],
        auth: { token },
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
      socket.on('connect', () => resolve(socket));
      socket.on('connect_error', reject);
    } catch (e) { reject(e); }
  });
}
